'use client';
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { CollapsibleMotion } from '../../../../components/Animate/Animate';
import Button from '../../../../components/Button/Button';
import Section from '../../../../components/Section/Section';
import { t } from '../../../../i18n';
import { toDate } from '../HybridActivityMap/hybridActivityDates';
import { parseSources } from '../HybridActivityMap/hybridActivitySources';
import { chipInk } from '../../../../utils/chipInk';

import s from './HybridActivityLog.module.scss';

// Volume is unknown, so the log renders in batches behind "show more" — cheap
// when the data is small, safe when it is not.
const BATCH_SIZE = 50;

// The years the data spans, for the title. One year names itself once.
const toYearSpan = (dateExtent) => {
    if (!dateExtent) {
        return null;
    }

    const from = dateExtent.min.slice(0, 4);
    const to = dateExtent.max.slice(0, 4);

    return from === to ? from : `${from}-${to}`;
};

// "30 Mar 2026", the design's format, off the row's already-validated
// YYYY-MM-DD. Pinned to UTC so no timezone can shift the calendar day.
const formatDate = (date) =>
    new Date(`${date}T00:00:00Z`).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
    });

// Newest first. Every row here carries a date — undated ones never make the
// log — and YYYY-MM-DD sorts chronologically as text.
const byNewestFirst = (a, b) => {
    if (a.date === b.date) {
        return 0;
    }

    return a.date > b.date ? -1 : 1;
};

// The incidents: rows whose layer declares dates — the same declaration the
// window reads — and whose own date cell parses. An asset such as
// infrastructure is a pin on the map, not an entry in a log of events.
// Every feature reaching here is one a configured layer claims — the tab only
// hands over configured points — so the layer lookup always lands.
const selectLogRows = (points, config) => {
    const layersByValue = new Map(
        config.layers.map((layer) => [layer.matchValue, layer])
    );

    return (points?.features || [])
        .map((feature) => {
            const layer = layersByValue.get(
                feature.properties?.[config.column]
            );

            return {
                properties: feature.properties || {},
                layer,
                date: layer?.isDated ? toDate(feature.properties?.date) : null,
            };
        })
        .filter((logRow) => logRow.date !== null)
        .sort(byNewestFirst);
};

/**
 * One incident as a card: the layer's chip over the date on the left, the
 * point's type as the headline with the info text under it. A row with
 * anything held back — location, the analyst's status, the `;`-separated
 * sources — is the button that opens it, the whole card and not only the
 * icon at its edge: the card is what a reader is already pointing at, and it
 * says so by darkening under the cursor the way a report card does. The icon
 * stays, because a surface that only answers to the pointer says nothing on a
 * touch screen. A row holding nothing back is not a button at all.
 */
const LogRow = ({ date, properties, layer, isExpanded, onToggle }) => {
    const { type, info, status, location } = properties;
    const sources = parseSources(properties.sources);
    const hasDetails =
        Boolean(location) || Boolean(status) || sources.length > 0;

    const main = (
        <>
            <div className={s.RowAside}>
                {/* The chip hugs its name inside a fixed-width column, so the
                    headlines align; a name longer than the column ellipsises,
                    with the full name on hover. */}
                <span
                    className={s.Chip}
                    data-ink={chipInk(layer.colour)}
                    style={{ backgroundColor: layer.colour }}
                    title={layer.name}>
                    {layer.name}
                </span>
                <span className={s.RowDate}>{formatDate(date)}</span>
            </div>

            <div className={s.RowBody}>
                {type && <h3 className={s.RowTitle}>{type}</h3>}
                {info && <p className={s.RowInfo}>{info}</p>}
            </div>
        </>
    );

    return (
        <li
            className={classNames(s.Row, {
                [s['Row--Expandable']]: hasDetails,
            })}>
            {hasDetails ? (
                <button
                    type="button"
                    className={classNames(s.RowMain, s.RowButton)}
                    aria-expanded={isExpanded}
                    onClick={onToggle}>
                    {main}
                    <span className={s.RowIcon} aria-hidden="true" />
                </button>
            ) : (
                <div className={s.RowMain}>{main}</div>
            )}

            {/* Slid open and closed the way the map's filter sections are;
                unlike their AnimateUpDown this variant also unmounts what is
                closed, so collapsed details stay out of the accessibility
                tree. */}
            <CollapsibleMotion isOpen={isExpanded}>
                <dl className={s.RowDetails}>
                    {location && (
                        <div className={s.Detail}>
                            <dt className={s.DetailName}>
                                {t(
                                    'watchtowerCountryPage.hybridActivityLogLocation'
                                )}
                            </dt>
                            <dd className={s.DetailValue}>{location}</dd>
                        </div>
                    )}

                    {status && (
                        <div className={s.Detail}>
                            <dt className={s.DetailName}>
                                {t(
                                    'watchtowerCountryPage.hybridActivityLogStatus'
                                )}
                            </dt>
                            <dd className={s.DetailValue}>{status}</dd>
                        </div>
                    )}

                    {sources.length > 0 && (
                        <div className={s.Detail}>
                            <dt className={s.DetailName}>
                                {t(
                                    'watchtowerCountryPage.hybridActivityLogSources'
                                )}
                            </dt>
                            <dd className={s.DetailValue}>
                                <ul className={s.SourceList}>
                                    {sources.map((source) => (
                                        <li key={source.url}>
                                            <a
                                                className={s.Source}
                                                href={source.url}
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                {source.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </dd>
                        </div>
                    )}
                </dl>
            </CollapsibleMotion>
        </li>
    );
};

LogRow.propTypes = {
    // The row's date as YYYY-MM-DD, already validated — an unreadable or
    // absent date keeps a row out of the log altogether.
    date: PropTypes.string.isRequired,
    // The dataset row, straight off the GeoJSON feature the map plots.
    properties: PropTypes.shape({
        type: PropTypes.string,
        info: PropTypes.string,
        status: PropTypes.string,
        location: PropTypes.string,
        sources: PropTypes.string,
    }).isRequired,
    // The configured layer the row belongs to, as the CMS authored it.
    layer: PropTypes.shape({
        name: PropTypes.string,
        icon: PropTypes.string,
        colour: PropTypes.string,
    }).isRequired,
    isExpanded: PropTypes.bool,
    onToggle: PropTypes.func.isRequired,
};

/**
 * The map's incidents as a list: the dated rows the pins are drawn from, in
 * the same date window the filter has applied, newest first. The map answers
 * "where?"; the log answers "what exactly happened?". Undated rows — assets,
 * by their layer's declaration — stay pins on the map and never log.
 *
 * Rows in layers hidden on the map still appear — layer visibility is a
 * map-drawing concern; the filter that scopes both views is the date window.
 */
const HybridActivityLog = ({ points, pointsInWindow, config, dateExtent }) => {
    const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
    // Rows are keyed by their position in the list, so when the window
    // reshuffles what sits at each position, what was shown for the old rows
    // must not carry over to the new ones.
    const [expandedIndexes, setExpandedIndexes] = useState(() => new Set());
    const [listedPoints, setListedPoints] = useState(pointsInWindow);

    if (pointsInWindow !== listedPoints) {
        setListedPoints(pointsInWindow);
        setVisibleCount(BATCH_SIZE);
        setExpandedIndexes(new Set());
    }

    const rows = useMemo(
        () => selectLogRows(pointsInWindow, config),
        [pointsInWindow, config]
    );
    // The loggable rows regardless of window, deciding whether the log exists
    // at all: data that is all assets will never produce an incident.
    const loggableRows = useMemo(
        () => selectLogRows(points, config),
        [points, config]
    );

    // With nothing to ever log there is no log — the map's empty state covers
    // an empty dataset, and an asset-only one needs no second list. A window
    // that filters everything out is different: the log stays, and says the
    // window is what emptied it.
    if (!loggableRows.length) {
        return null;
    }

    const yearSpan = toYearSpan(dateExtent);

    const toggleRow = (index) =>
        setExpandedIndexes((expanded) => {
            const next = new Set(expanded);

            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }

            return next;
        });

    return (
        <Section modifier="GreyDesktop">
            <div className={s.Log}>
                <h2 className={s.Title}>
                    <span>
                        {t('watchtowerCountryPage.hybridActivityLogTitle')}
                    </span>
                    {yearSpan && (
                        <span className={s.TitleYears}>({yearSpan})</span>
                    )}
                </h2>

                {rows.length === 0 ? (
                    <p className={s.Empty}>
                        {t('watchtowerCountryPage.hybridActivityLogEmpty')}
                    </p>
                ) : (
                    <ul
                        className={s.List}
                        aria-label={t(
                            'watchtowerCountryPage.hybridActivityLogTitle'
                        )}>
                        {rows.slice(0, visibleCount).map((logRow, index) => (
                            <LogRow
                                key={index}
                                date={logRow.date}
                                properties={logRow.properties}
                                layer={logRow.layer}
                                isExpanded={expandedIndexes.has(index)}
                                onToggle={() => toggleRow(index)}
                            />
                        ))}
                    </ul>
                )}

                {rows.length > visibleCount && (
                    <div className={s.ShowMore}>
                        <Button
                            modifier="Secondary"
                            size="Large"
                            title={t(
                                'watchtowerCountryPage.hybridActivityLogShowMore'
                            )}
                            onClick={() =>
                                setVisibleCount((count) => count + BATCH_SIZE)
                            }
                        />
                    </div>
                )}
            </div>
        </Section>
    );
};

const featureCollection = PropTypes.shape({
    type: PropTypes.string,
    features: PropTypes.array,
});

HybridActivityLog.propTypes = {
    // Every configured point, and the subset inside the applied window — the
    // same two sets the map reads, so both views tell one story.
    points: featureCollection,
    pointsInWindow: featureCollection,
    config: PropTypes.shape({
        column: PropTypes.string,
        layers: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                matchValue: PropTypes.string,
                icon: PropTypes.string,
                colour: PropTypes.string,
                isDated: PropTypes.bool,
            })
        ),
    }).isRequired,
    // The dated points' full extent, for the years beside the title.
    dateExtent: PropTypes.shape({
        min: PropTypes.string,
        max: PropTypes.string,
    }),
};

export default HybridActivityLog;
